import { getSupabase } from '../../supabaseClient';
import {
  upsertDmMessageNotification,
  upsertGroupMessageNotifications,
} from '../../utils/notifications';

function isUniqueViolation(error) {
  return error?.code === '23505';
}

/**
 * Insert a message with a client-generated id for idempotent replay.
 * A duplicate insert (timeout replay) resolves to the existing row.
 */
export async function insertMessageIdempotent(payload, { notify } = {}) {
  const supabase = getSupabase();
  const id = payload.id ?? crypto.randomUUID();
  const row = { ...payload, id };

  const { data, error } = await supabase.from('messages').insert(row).select().maybeSingle();

  if (error) {
    if (isUniqueViolation(error)) {
      const { data: existing, error: fetchErr } = await supabase
        .from('messages')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (fetchErr) throw fetchErr;
      if (existing) return existing;
    }
    throw error;
  }

  if (data && notify) {
    await notify(data);
  }

  return data;
}

export async function sendDmMessageMutationFn(variables) {
  const { messageId, userId, recipientUserId, content, conversationId, senderName } = variables;

  return insertMessageIdempotent(
    {
      id: messageId,
      conversation_type: 'dm',
      group_id: null,
      user_id: userId,
      recipient_user_id: recipientUserId,
      message_type: 'user',
      content,
    },
    {
      notify: async () => {
        if (!conversationId || !senderName) return;
        await upsertDmMessageNotification({
          supabase: getSupabase(),
          recipientUserId,
          senderId: userId,
          senderName,
          conversationId,
          messagePreview: `${senderName}: ${content.slice(0, 90)}`,
        });
      },
    },
  );
}

export async function sendGroupMessageMutationFn(variables) {
  const { messageId, groupId, userId, content, recipientIds, groupName, senderName } = variables;

  const supabase = getSupabase();

  const { data: membershipCheck, error: membershipError } = await supabase
    .from('group_members')
    .select('id')
    .eq('group_id', groupId)
    .eq('user_id', userId)
    .is('left_at', null)
    .maybeSingle();

  if (membershipError) throw membershipError;

  if (!membershipCheck) {
    const err = new Error('Not an active group member');
    err.status = 403;
    throw err;
  }

  return insertMessageIdempotent(
    {
      id: messageId,
      group_id: groupId,
      user_id: userId,
      message_type: 'user',
      conversation_type: 'group',
      content,
    },
    {
      notify: async () => {
        if (!recipientIds?.length || !senderName) return;
        await upsertGroupMessageNotifications({
          supabase,
          recipientIds,
          senderId: userId,
          groupId,
          groupName: groupName || 'your group',
          messagePreview: `${senderName}: ${content.slice(0, 90)}`,
        });
      },
    },
  );
}

export function createOptimisticMessage({
  messageId,
  userId,
  content,
  profile,
  conversationType,
  recipientUserId,
  groupId,
}) {
  return {
    id: messageId,
    user_id: userId,
    content,
    message_type: 'user',
    conversation_type: conversationType,
    recipient_user_id: recipientUserId ?? null,
    group_id: groupId ?? null,
    created_at: new Date().toISOString(),
    profile: profile ?? null,
    _localSendStatus: 'pending',
  };
}
